/* eslint-disable fp/no-mutation */
import httpClient, { AxiosError, AxiosResponse } from 'axios'
import { getTokens } from './auth0Client'

httpClient.interceptors.request.use(
  async (request) => {
    if (
      !request.url?.includes('/register') &&
      !request.url?.includes('/external-survey') &&
      !request.url?.includes('/api/subscription/user') &&
      // !request.url?.includes('/api/promo/validate') &&
      request.url?.indexOf('/api') === 0
    ) {
      let { accessToken } = await getTokens()
      if (accessToken) {
        request.headers.Authorization = `Bearer ${accessToken}`
      }
    }

    if (request.url?.indexOf('/api') === 0) {
      request.url = `${import.meta.env.VITE_BACKEND_URL}${request.url}`
    }

    return request
  },
  (error) => Promise.reject(error),
)

httpClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.toString()?.includes('status code 401')) {
      localStorage.removeItem('jwt') // This line may become redundant if using the Auth0 client to manage tokens
      if (
        !window.location.pathname.includes('/register') &&
        !window.location.pathname.includes('/boundtree') &&
        !window.location.pathname.includes('/boundtree-trial') &&
        !window.location.pathname.includes('/boundtree-login') &&
        !window.location.pathname.includes('/subscribe') &&
        !window.location.pathname.includes('/subscribe-boundtree') &&
        !window.location.pathname.includes('/payment-failed') &&
        !window.location.pathname.includes('/external-survey')
      ) {
        window.location.href = '/'
      }
    }
    return Promise.reject(error)
  },
)

export let pluckData = <T>(response: AxiosResponse<T>) => response.data
export let pluckError = <T>(error: AxiosError<T>, alt?: string) =>
  error.response?.data || error.message || alt || 'Error making request'

let EXCLUDED_AUTH_PATHS = [
  '/register',
  '/external-survey',
  '/api/subscription/user',
]

type StreamRequestOptions = {
  method?: string
  body?: unknown
  onChunk: (chunk: string, full: string) => void
}

// Mirrors the auth/base-url wiring of the axios interceptors above, but reads
// the response body incrementally instead of buffering it — for endpoints
// that stream a long-running LLM response back to the caller.
export let streamRequest = async (
  url: string,
  { method = 'POST', body, onChunk }: StreamRequestOptions,
) => {
  let headers: Record<string, string> = { 'Content-Type': 'application/json' }

  if (
    !EXCLUDED_AUTH_PATHS.some((path) => url.includes(path)) &&
    url.indexOf('/api') === 0
  ) {
    let { accessToken } = await getTokens()
    if (accessToken) headers.Authorization = `Bearer ${accessToken}`
  }

  let fullUrl =
    url.indexOf('/api') === 0
      ? `${import.meta.env.VITE_BACKEND_URL}${url}`
      : url

  let res = await fetch(fullUrl, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })

  if (!res.ok) {
    let data = await res.json().catch(() => null)
    // eslint-disable-next-line fp/no-throw, no-throw-literal
    throw { response: { status: res.status, data } }
  }

  if (!res.body) {
    // eslint-disable-next-line fp/no-throw
    throw new Error('Streaming response had no body')
  }

  let reader = res.body.getReader()
  let decoder = new TextDecoder()

  let readChunk = async (full: string): Promise<string> => {
    let { done, value } = await reader.read()
    if (done) return full
    let chunk = decoder.decode(value, { stream: true })
    let next = full + chunk
    onChunk(chunk, next)
    return readChunk(next)
  }

  return readChunk('')
}

export default httpClient
